import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fdate } from "@/lib/format";
import { Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Printer, ScrollText, GripVertical } from "lucide-react";

const PALETTES = [
  { id: "ivory", name: "Ivoire", bg: "#F7EFE3", fg: "#4A3B2A", accent: "#CBA35C" },
  { id: "sage", name: "Sauge", bg: "#EEF2E9", fg: "#33402B", accent: "#657668" },
  { id: "noir", name: "Noir", bg: "#111110", fg: "#F4F0EA", accent: "#D4AF37" },
  { id: "alps", name: "Alpin", bg: "#EDEEF0", fg: "#22303A", accent: "#3E7CB1" },
];

const DEFAULT_MOMENTS = [
  { time: "15:00", title: "Accueil des invités", desc: "Verre de bienvenue" },
  { time: "15:30", title: "Cérémonie", desc: "Échange des vœux et des alliances" },
  { time: "16:15", title: "Sortie des mariés", desc: "Haie d'honneur & photos" },
  { time: "17:00", title: "Vin d'honneur", desc: "Cocktail et animations" },
  { time: "19:30", title: "Dîner", desc: "" },
  { time: "23:00", title: "Ouverture du bal", desc: "Première danse" },
];

export default function CeremonyEditor() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const w = wedding?.wedding;
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get(`/weddings/${wid}/invitations`);
    const existing = data.find((d) => d.type === "ceremony");
    if (existing) {
      existing.config = { palette: "ivory", moments: DEFAULT_MOMENTS, ...existing.config };
      setDesign(existing);
    } else setDesign(null);
    setLoading(false);
  };
  useEffect(() => { if (wid) load(); }, [wid]);

  const createProgram = async () => {
    const { data } = await api.post(`/weddings/${wid}/invitations`, {
      type: "ceremony", template: "ceremony", title: "Programme de la journée",
      config: { palette: "ivory", header: "Programme", subheader: w?.names || "", date: w?.wedding_date || "",
        celebrant: "", officiant_note: "", moments: DEFAULT_MOMENTS },
    });
    data.config = { palette: "ivory", moments: DEFAULT_MOMENTS, ...data.config };
    setDesign(data);
  };

  const save = async (next) => {
    setDesign(next);
    await api.patch(`/weddings/${wid}/invitations/${next.id}`, { config: next.config });
  };
  const setCfg = (patch) => save({ ...design, config: { ...design.config, ...patch } });
  const setMoment = (i, patch) => setCfg({ moments: design.config.moments.map((m, x) => (x === i ? { ...m, ...patch } : m)) });
  const addMoment = () => setCfg({ moments: [...(design.config.moments || []), { time: "", title: "Nouveau moment", desc: "" }] });
  const delMoment = (i) => setCfg({ moments: design.config.moments.filter((_, x) => x !== i) });
  const move = (i, dir) => {
    const arr = [...design.config.moments];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setCfg({ moments: arr });
  };

  if (loading) return <Loader />;
  if (!design) return (
    <div className="card-lux p-10 text-center max-w-lg mx-auto">
      <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4"><ScrollText className="w-6 h-6" /></div>
      <h3 className="font-serif text-2xl mb-2">Programme de cérémonie</h3>
      <p className="text-sm text-stone-500 mb-6">Détaillez le déroulé de votre journée, moment par moment, pour guider vos invités.</p>
      <Button onClick={createProgram} className="rounded-full" data-testid="create-ceremony"><Plus className="w-4 h-4 mr-1" /> Créer mon programme</Button>
    </div>
  );

  const pal = PALETTES.find((p) => p.id === design.config.palette) || PALETTES[0];
  const FB = { bg: pal.bg, fg: pal.fg, title: pal.fg, accent: pal.accent };
  const C = (k) => (design.config.colors?.[k]) || FB[k];
  const style = design.config.style || "timeline";
  const setColor = (k, v) => setCfg({ colors: { ...(design.config.colors || {}), [k]: v } });

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-lux p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1"><ScrollText className="w-4 h-4" /><h3 className="font-serif text-lg">Déroulé de la journée</h3></div>
        <div><Label>Titre</Label><Input value={design.config.header || ""} onChange={(e) => setCfg({ header: e.target.value })} className="mt-1 h-10" data-testid="ceremony-header" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Noms</Label><Input value={design.config.subheader || ""} onChange={(e) => setCfg({ subheader: e.target.value })} className="mt-1 h-10" /></div>
          <div><Label>Célébrant·e</Label><Input value={design.config.celebrant || ""} onChange={(e) => setCfg({ celebrant: e.target.value })} className="mt-1 h-10" placeholder="Optionnel" /></div>
        </div>
        <div><Label>Palette</Label>
          <div className="flex gap-2 mt-1.5">
            {PALETTES.map((p) => (
              <button key={p.id} onClick={() => setCfg({ palette: p.id })} data-testid={`ceremony-pal-${p.id}`}
                className={`w-9 h-9 rounded-full border-2 ${design.config.palette === p.id ? "ring-2 ring-champagne" : ""}`}
                style={{ background: p.bg, borderColor: p.accent }} title={p.name} />
            ))}
          </div></div>
        <div><Label>Couleurs personnalisées</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {[["bg", "Fond"], ["fg", "Texte"], ["title", "Titres"], ["accent", "Ornements"]].map(([k, l]) => (
              <div key={k} className="flex items-center gap-2">
                <input type="color" value={C(k)} onChange={(e) => setColor(k, e.target.value)} data-testid={`ceremony-color-${k}`} className="w-8 h-8 rounded border p-0.5 cursor-pointer" />
                <div className="flex-1"><div className="text-[10px] text-stone-400">{l}</div>
                  <Input value={C(k)} onChange={(e) => setColor(k, e.target.value)} className="h-7 text-xs font-mono" /></div>
              </div>
            ))}
          </div></div>
        <div><Label>Style</Label>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            {[["timeline", "Timeline"], ["timeline_icons", "Timeline + picto"], ["classic", "Classique"], ["minimal", "Minimaliste"], ["floral", "Floral"], ["moderne", "Moderne"], ["artdeco", "Art Déco"]].map(([k, l]) => (
              <button key={k} onClick={() => setCfg({ style: k })} data-testid={`ceremony-style-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${style === k ? "bg-stone-900 text-white border-stone-900" : "bg-white"}`}>{l}</button>
            ))}
          </div></div>
        <div className="border-t pt-3 space-y-2">
          {(design.config.moments || []).map((m, i) => (
            <div key={i} className="border rounded-xl p-3" data-testid={`moment-${i}`}>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} className="text-stone-300 hover:text-stone-700 text-xs">▲</button>
                  <button onClick={() => move(i, 1)} className="text-stone-300 hover:text-stone-700 text-xs">▼</button>
                </div>
                <Input value={m.time} onChange={(e) => setMoment(i, { time: e.target.value })} className="h-9 w-20" placeholder="15:00" data-testid={`moment-time-${i}`} />
                <Input value={m.title} onChange={(e) => setMoment(i, { title: e.target.value })} className="h-9 flex-1 font-medium" placeholder="Titre" />
                <button onClick={() => delMoment(i)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              <Input value={m.desc} onChange={(e) => setMoment(i, { desc: e.target.value })} className="h-8 text-sm mt-2" placeholder="Description (optionnel)" />
            </div>
          ))}
          <Button variant="outline" onClick={addMoment} className="w-full rounded-full" data-testid="add-moment"><Plus className="w-4 h-4 mr-1" /> Ajouter un moment</Button>
        </div>
        <Button variant="outline" className="w-full rounded-full" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Imprimer / PDF</Button>
      </div>

      <div className="flex items-start justify-center">
        <div className="w-full max-w-sm aspect-[3/4] rounded-2xl shadow-xl overflow-hidden flex flex-col" style={{ background: C("bg"), color: C("fg") }} data-testid="ceremony-preview">
          {style === "artdeco" && <div className="h-2" style={{ background: C("accent") }} />}
          <div className="p-8 flex-1 overflow-y-auto">
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: C("accent") }}>{design.config.subheader}</div>
              <div className={`font-serif mt-1 ${style === "artdeco" ? "uppercase tracking-[0.3em] text-2xl" : style === "moderne" ? "text-4xl font-bold" : "text-3xl"}`} style={{ color: C("title") }}>{design.config.header || "Programme"}</div>
              {design.config.date && <div className="text-xs mt-1 opacity-70">{fdate(design.config.date)}</div>}
              {style !== "minimal" && <div className="w-12 h-px mx-auto my-4" style={{ background: C("accent") }} />}
            </div>
            <div className={style === "floral" ? "space-y-1" : "space-y-3"}>
              {(design.config.moments || []).map((m, i) => {
                if (style === "classic" || style === "floral") return (
                  <div key={i} className="text-center">
                    <div className="font-serif text-sm" style={{ color: C("accent") }}>{m.time}</div>
                    <div className="text-sm font-medium">{m.title}</div>
                    {m.desc && <div className="text-xs opacity-70">{m.desc}</div>}
                    {style === "floral" && i < design.config.moments.length - 1 && <div className="my-1" style={{ color: C("accent") }}>❀</div>}
                  </div>
                );
                if (style === "moderne") return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: C("accent"), color: C("bg") }}>{m.time}</span>
                    <div><div className="text-sm font-semibold">{m.title}</div>{m.desc && <div className="text-xs opacity-70">{m.desc}</div>}</div>
                  </div>
                );
                return (
                  <div key={i} className="flex gap-3">
                    <div className="font-serif text-sm w-12 shrink-0 text-right" style={{ color: C("accent") }}>{m.time}</div>
                    {style === "timeline_icons" && <span className="shrink-0 mt-1.5 w-2.5 h-2.5 rounded-full" style={{ background: C("accent") }} />}
                    <div className={style === "minimal" ? "pl-1" : "border-l pl-3"} style={{ borderColor: C("accent") }}>
                      <div className="text-sm font-medium">{m.title}</div>
                      {m.desc && <div className="text-xs opacity-70">{m.desc}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {design.config.celebrant && <div className="text-center text-xs italic mt-4 opacity-70">Célébré par {design.config.celebrant}</div>}
          </div>
          {style === "artdeco" && <div className="h-2" style={{ background: C("accent") }} />}
        </div>
      </div>
    </div>
  );
}
